import React from 'react';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import {
  NDCRecord,
  BreakLinkResponse,
} from '../../services/api/domains/products/types';
import { formatDateTime } from '../../utils/dateConversion';
import { parseNdcActiveIngredientNames } from '../../utils/ndcActiveIngredients';
import { formatDeaSchedule } from '../../utils/deaScheduleLabels';
import DeaScheduleHeaderTooltip from '../DeaScheduleHeaderTooltip';
import { GRAY_500, GRAY_900 } from 'theme/grayScale';
import { NDC_PANEL_COLORS } from 'theme/sourcePanelColors';
import ExpandableFieldList from './ExpandableFieldList';
import LgmButton from '../LgmButton';
import CollapsibleSection from './CollapsibleSection';
import SourcePanelEmptyState from './SourcePanelEmptyState';
import BreakLinkConfirmDialog from './BreakLinkConfirmDialog';

// ---------------------------------------------------------------------------
// Sub-component — a single read-only label/value row
// ---------------------------------------------------------------------------

const FieldRow: React.FC<{ label: React.ReactNode; value: string }> = ({
  label,
  value,
}) => (
  <div
    style={{
      display: 'flex',
      gap: '8px',
      padding: '4px 0',
      fontSize: '14px',
      lineHeight: '1.4',
    }}>
    <span
      style={{
        minWidth: '140px',
        fontWeight: 600,
        color: GRAY_500,
        flexShrink: 0,
      }}>
      {label}
    </span>
    <span style={{ color: GRAY_900, wordBreak: 'break-word' }}>{value}</span>
  </div>
);

function formatFinished(finished: boolean | null): string {
  if (finished === null || finished === undefined) return '—';
  return finished ? 'Finished' : 'Unfinished';
}

function recordLabel(record: NDCRecord): string {
  return record.product_ndc || record.generic_name || 'NDC record';
}

// ---------------------------------------------------------------------------
// NDCPanel — External Sources redesign, extended by issue #622 Task 8
// (three-state matrix, collapse behaviour, break-link control)
// ---------------------------------------------------------------------------

interface NDCPanelProps {
  productId: number;
  ndcRecords: NDCRecord[];
  /** Admin AND edit mode — both gate the break-link icon and the add-link control. */
  isAdmin: boolean;
  isEditMode: boolean;
  /**
   * Opens the Task 9 Link-to-Source modal, scoped to NDC. That modal
   * doesn't exist yet (issue #622 Task 9 is separate work) — this is a
   * placeholder trigger point only.
   */
  onAddLink: () => void;
}

/**
 * NDC (National Drug Code) section for the Product modal. Always renders
 * (issue #622, Sub-task 8.1) — like DMFPanel (and unlike OrangeBookPanel's
 * former single top-1 record), a product can legitimately link to several
 * NDC listings (different package sizes/labelers), so this renders one
 * block per record. FDA is the authoritative source — no NDC data here is
 * directly editable; the only write action is breaking a link (Sub-task 8.3).
 */
const NDCPanel: React.FC<NDCPanelProps> = ({
  productId,
  ndcRecords,
  isAdmin,
  isEditMode,
  onAddLink,
}) => {
  const [brokenIds, setBrokenIds] = React.useState<Set<number>>(new Set());
  const [breakTarget, setBreakTarget] = React.useState<{
    recordId: number;
    label: string;
  } | null>(null);

  // Also reset on a genuinely fresh `ndcRecords` array (a real refetch, e.g.
  // after Task 9's link-create refresh) -- not just productId. Otherwise
  // breaking then re-linking the SAME underlying record within one modal
  // session leaves it permanently filtered out here: brokenIds is additive
  // and this was its only reset path, so a resurrected record with the same
  // ndc_product_id stayed hidden even though fresh server data proved it
  // linked again. Found live via the Playwright MCP (issue #622).
  React.useEffect(() => {
    setBrokenIds(new Set());
    setBreakTarget(null);
  }, [productId, ndcRecords]);

  const canWrite = isAdmin && isEditMode;
  const records = ndcRecords.filter(r => !brokenIds.has(r.ndc_product_id));

  if (records.length === 0) {
    return (
      <SourcePanelEmptyState
        badgeText="FDA"
        sourceLabel="NDC"
        palette={NDC_PANEL_COLORS}
        canAdd={canWrite}
        onAdd={onAddLink}
      />
    );
  }

  const handleBroken = (result: BreakLinkResponse) => {
    setBrokenIds(prev => new Set(prev).add(result.record_id));
    setBreakTarget(null);
  };

  return (
    <div
      style={{
        margin: '20px 0 8px 0',
        border: `1px solid ${NDC_PANEL_COLORS.border}`,
        borderRadius: '6px',
        overflow: 'hidden',
        backgroundColor: NDC_PANEL_COLORS.background,
      }}>
      {/* Panel header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          padding: '8px 12px',
          backgroundColor: NDC_PANEL_COLORS.headerBackground,
          borderBottom: `1px solid ${NDC_PANEL_COLORS.border}`,
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span
            style={{
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: NDC_PANEL_COLORS.badgeText,
              backgroundColor: NDC_PANEL_COLORS.badgeBackground,
              padding: '2px 6px',
              borderRadius: '4px',
            }}>
            FDA - NDC
          </span>
        </div>
        {/* A source can legitimately have multiple links -- the zero-link
            empty state's add button (SourcePanelEmptyState) only covers
            going from 0 to 1. Without this, an admin with an existing link
            has no way to add another. Found live via the Playwright MCP
            (issue #622). */}
        {canWrite && (
          <LgmButton
            variant="outlined"
            onClick={onAddLink}
            style={{ fontSize: '12px', padding: '4px 10px' }}>
            + Link to NDC
          </LgmButton>
        )}
      </div>

      <div style={{ padding: '8px 12px' }}>
        <CollapsibleSection
          title={`${records.length} link${records.length === 1 ? '' : 's'}`}
          defaultCollapsed={records.length > 1}>
          {records.map((record, idx) => (
            <div
              key={`${record.ndc_product_id}-${idx}`}
              style={{
                borderTop:
                  idx > 0 ? `1px solid ${NDC_PANEL_COLORS.border}` : undefined,
                paddingTop: idx > 0 ? '8px' : undefined,
                marginTop: idx > 0 ? '8px' : undefined,
              }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '8px',
                }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <FieldRow label="NDC" value={record.product_ndc ?? '—'} />
                  {/* Relabeled from "Active Ingredient" (issue #585 Task 9.2) — this
                      is the raw generic_name field, distinct from the real
                      active_ingredients list below and from the grid/search
                      ndc_active_ingredient field, which aggregates the list across
                      every linked NDC record, not this one record's raw name. */}
                  <FieldRow
                    label="Generic Name"
                    value={record.generic_name ?? '—'}
                  />
                  <ExpandableFieldList
                    label="Active Ingredients"
                    items={parseNdcActiveIngredientNames(
                      record.active_ingredients
                    )}
                  />
                  <FieldRow
                    label="Labeler"
                    value={record.labeler_name ?? '—'}
                  />
                  <FieldRow
                    label="Product Type"
                    value={record.product_type ?? '—'}
                  />
                  <FieldRow
                    label="Finished/Unfinished"
                    value={formatFinished(record.finished)}
                  />
                  <FieldRow
                    label={
                      <>
                        DEA Schedule
                        <DeaScheduleHeaderTooltip />
                      </>
                    }
                    value={formatDeaSchedule(record.dea_schedule)}
                  />
                  <FieldRow
                    label="Marketing Start"
                    value={formatDateTime(record.marketing_start_date) || '—'}
                  />
                  <FieldRow
                    label="Marketing End"
                    value={formatDateTime(record.marketing_end_date) || '—'}
                  />
                  <FieldRow
                    label="Listing Expiration"
                    value={
                      formatDateTime(record.listing_expiration_date) || '—'
                    }
                  />
                </div>
                {canWrite && (
                  <LgmButton
                    variant="flat"
                    color="danger"
                    onClick={() =>
                      setBreakTarget({
                        recordId: record.ndc_product_id,
                        label: recordLabel(record),
                      })
                    }
                    aria-label={`Break link to NDC record ${recordLabel(record)}`}
                    style={{ padding: '4px', minWidth: 'auto', flexShrink: 0 }}>
                    <LinkOffIcon style={{ fontSize: '18px' }} />
                  </LgmButton>
                )}
              </div>
            </div>
          ))}
        </CollapsibleSection>
      </div>

      {breakTarget && (
        <BreakLinkConfirmDialog
          productId={productId}
          source="ndc"
          sourceLabel="NDC"
          recordId={breakTarget.recordId}
          recordLabel={breakTarget.label}
          onClose={() => setBreakTarget(null)}
          onBroken={handleBroken}
        />
      )}
    </div>
  );
};

export default NDCPanel;
